import { Box, Flex } from '@components/grid';
import { InputListItem } from '@components/input-list-item';
import { BASTINGS, BASTING_TYPES_BY_NAME, colors } from '@nandosaus/constants';
import { getBastingKey } from '@utils/get-basting-key/get-basting-key';
import { trim } from 'lodash';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'motion/react';
import PeriometerSVG from './periometer';

import { OptionPropType, SelectedOptionsPropType } from './prop-types';
import { P } from '../typography/new';

const getBastingHelperText = ({ maxSelection, selectedCount }) => {
  if (selectedCount < maxSelection) {
    if (maxSelection === 2) {
      return 'Choose your basting, or mix two together!';
    }

    return `You can select a maximum of ${maxSelection} ${maxSelection > 1 ? 'options' : 'option'}.`;
  }

  return `You selected ${selectedCount}/${maxSelection} ${
    selectedCount > 1 ? 'options' : 'option'
  }. Deselect an option to change your selection.`;
};

const BastingSelector = ({
  bastingOptions,
  selectedOptions,
  onChange,
  isMixedMode = false,
  maxSelection = 1,
  plainishOption = null,
}) => {
  const selectedBastings = bastingOptions.filter(({ value }) => selectedOptions.includes(value));
  const selectedBastingKeys = selectedBastings.map(basting => getBastingKey(basting)).filter(Boolean);
  const selectedDescriptions = selectedBastingKeys.map(key => BASTINGS[key]?.description).filter(Boolean);

  const isPlainishSelected = plainishOption && selectedOptions.includes(plainishOption.value);
  const selectedNonPlainish = plainishOption
    ? selectedOptions.filter(value => value !== plainishOption.value)
    : selectedOptions;

  const handleSelect = itemValue => {
    if (isMixedMode) {
      const isSelected = selectedOptions.includes(itemValue);
      if (isSelected) {
        onChange(selectedOptions.filter(value => value !== itemValue));
      } else if (selectedNonPlainish.length < maxSelection) {
        // Remove plainish if it's selected when adding a new basting option
        const newSelection = selectedNonPlainish.concat(itemValue);
        onChange(newSelection);
      }
    } else {
      onChange(itemValue);
    }
  };

  const handlePlainishSelect = () => {
    if (isPlainishSelected) {
      onChange(selectedNonPlainish);
    } else {
      onChange([plainishOption.value]);
    }
  };

  return (
    <Flex width="100%" flexDirection="column">
      {isMixedMode && (
        <P variant="standard" textAlign="left" mt="4px" mb="24px">
          {getBastingHelperText({
            maxSelection,
            selectedCount: selectedNonPlainish.length,
          })}
        </P>
      )}
      <Flex position="relative" pt={!isMixedMode && '8px'} justifyContent="space-between" width="100%">
        <Box flex="1 1 auto" width="100%" flexDirection="column" data-testid="input-list" pointerEvents="auto">
          <Flex flexDirection="column" gap="16px">
            {bastingOptions.map(item => {
              const key = item.value;
              const isSelected = !!selectedOptions.find(select => select === item.value);
              const isDisabled = isMixedMode && !isSelected && selectedNonPlainish.length >= maxSelection;
              const bastingKey = BASTING_TYPES_BY_NAME[trim(item.label)];
              return (
                <InputListItem
                  key={key}
                  borderBottomWidth="0"
                  icon={item.icon}
                  label={item.label}
                  py={0}
                  labelProps={{
                    color: colors[bastingKey],
                    fontFamily: 'brand',
                    fontWeight: 'normal',
                    textTransform: 'none',
                  }}
                  onSelect={() => handleSelect(item.value)}
                  selected={isSelected}
                  disabled={isDisabled || item.disabled}
                  type={isMixedMode ? 'checkbox' : 'radio'}
                />
              );
            })}

            {plainishOption && (
              <>
                <P pt="4px" variant="standard" textAlign="left">
                  Or are you traditional? select:
                </P>
                <InputListItem
                  borderBottomWidth="0"
                  label={plainishOption.label}
                  py={0}
                  labelProps={{
                    color: colors[BASTING_TYPES_BY_NAME[trim(plainishOption.label)]],
                    fontFamily: 'brand',
                    fontWeight: 'normal',
                    textTransform: 'none',
                  }}
                  onSelect={handlePlainishSelect}
                  selected={isPlainishSelected}
                  disabled={plainishOption.disabled}
                  type="radio"
                />
              </>
            )}
          </Flex>
          <Box
            position="relative"
            mt="16px"
            width="100%"
            height={selectedDescriptions.length > 0 ? `${selectedDescriptions.length * 32 + 16}px` : '0'}
          >
            <AnimatePresence mode="wait">
              {selectedDescriptions.length > 0 && (
                <motion.div
                  key={selectedDescriptions.join('-')}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{
                    duration: 0.3,
                    ease: 'easeOut',
                    delay: 0.2,
                  }}
                  style={{ position: 'absolute', top: 0, left: 0, right: 0 }}
                >
                  <Box pr="16px">
                    {selectedDescriptions.map((desc, i) => (
                      <Flex
                        as="span"
                        flex={1}
                        justifyContent="flex-end"
                        key={desc}
                        mb={i === selectedDescriptions.length - 1 ? 0 : '8px'}
                      >
                        <P variant="small-semi-bold" minWidth="fit-content" mr="4px">
                          {bastingOptions
                            .filter(({ value }) => selectedOptions.includes(value))
                            [i]?.label?.replace(/\s+$/, '')}
                          {'-'}{' '}
                          <P as="span" variant="small">
                            {desc}
                          </P>
                        </P>
                      </Flex>
                    ))}
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
        </Box>
        <Flex flex="1" justifyContent="flex-end" alignItems="center" position="relative">
          <PeriometerSVG />
        </Flex>
      </Flex>
    </Flex>
  );
};

BastingSelector.propTypes = {
  bastingOptions: PropTypes.arrayOf(OptionPropType).isRequired,
  selectedOptions: SelectedOptionsPropType.isRequired,
  onChange: PropTypes.func.isRequired,
  isMixedMode: PropTypes.bool,
  maxSelection: PropTypes.number,
  plainishOption: OptionPropType,
};

BastingSelector.defaultProps = {
  isMixedMode: false,
  maxSelection: 1,
  plainishOption: null,
};

export { BastingSelector };
